import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight } from 'lucide-react';
import type { OrderFulfilment, OrderLinesPage, OrderLineView } from '@his/shared';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Skeleton, StatusBadge } from '@/components/ui/primitives';
import { ReasonDialog } from '@/components/ui/reason-dialog';
import { useToast } from '@/components/ui/toast';

const PAGE_SIZE = 50;

function billingLabel(line: OrderLineView): { status: string; label: string } | null {
  if (line.fulfilment === 'outside' || line.status === 'cancelled') return null;
  if (line.billing === 'paid') return { status: 'paid', label: tr('billing', 'order_line_paid') };
  if (line.billing === 'unpaid') return { status: 'awaiting', label: tr('billing', 'order_line_awaiting_payment') };
  return { status: 'draft', label: tr('billing', 'order_line_not_billed') };
}

export function VisitOrderLinesCard({ encounterId }: { encounterId: string }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [switching, setSwitching] = useState<OrderLineView | null>(null);
  const lines = useInfiniteQuery({
    queryKey: ['order-lines', encounterId, 'desk'],
    initialPageParam: '',
    queryFn: ({ pageParam, signal }) => get<OrderLinesPage>(`/clinical-orders/lines${qs({ encounterId, limit: PAGE_SIZE, cursor: pageParam || undefined })}`, signal),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = (lines.data?.pages.flatMap((page) => page.data) ?? []).filter((line) => line.status !== 'cancelled');
  const target: OrderFulfilment = switching?.fulfilment === 'outside' ? 'in_house' : 'outside';

  const change = useMutation({
    mutationFn: ({ id, fulfilment, reason }: { id: string; fulfilment: OrderFulfilment; reason: string }) => post(`/clinical-orders/lines/${id}/fulfilment`, { fulfilment, reason }),
    onSuccess: () => {
      setSwitching(null);
      toast.success(tr('billing', 'order_line_switched'));
      void queryClient.invalidateQueries({ queryKey: ['order-lines', encounterId] });
      void queryClient.invalidateQueries({ queryKey: ['statement'] });
    },
    onError: (caught) => toast.error(tr('billing', 'order_line_could_not_switch'), describeError(caught)),
  });

  if (!lines.isLoading && rows.length === 0 && !lines.error) return null;

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-sm">{tr('billing', 'visit_order_lines')}</CardTitle>
        <p className="text-xs text-muted-foreground">{tr('billing', 'visit_order_lines_hint')}</p>
      </CardHeader>
      <CardContent>
        {lines.isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : lines.error && rows.length === 0 ? (
          <p className="text-sm text-destructive">{describeError(lines.error)}</p>
        ) : (
          <ul className="divide-y">
            {rows.map((line) => {
              const bill = billingLabel(line);
              const outside = line.fulfilment === 'outside';
              return (
                <li key={line.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {line.label}
                      {line.quantity > 1 && <span className="ms-1.5 text-xs font-normal text-muted-foreground tabular">×{line.quantity}</span>}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {tr('billing', `order_line_type_${line.type}`)}
                      {line.invoices.map((invoice) => (
                        <span key={invoice.id}>
                          {' · '}
                          <Link className="underline" to={`/billing/invoices/${invoice.id}`}>
                            {invoice.number}
                          </Link>
                        </span>
                      ))}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <StatusBadge status={line.status} />
                    <Badge variant={outside ? 'warning' : 'info'}>{outside ? tr('billing', 'order_line_outside') : tr('billing', 'order_line_done_here')}</Badge>
                    {bill && <StatusBadge status={bill.status} label={bill.label} tone={bill.status === 'draft' ? 'muted' : undefined} />}
                    {line.switchable && can('invoice.create') && (
                      <Button size="sm" variant="outline" onClick={() => setSwitching(line)}>
                        <ArrowLeftRight className="h-4 w-4" aria-hidden /> {outside ? tr('billing', 'order_line_do_here') : tr('billing', 'order_line_send_outside')}
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {lines.hasNextPage && (
          <div className="mt-3 flex justify-center">
            <Button size="sm" variant="outline" onClick={() => void lines.fetchNextPage()} loading={lines.isFetchingNextPage}>
              {tr('billing', 'order_lines_load_more')}
            </Button>
          </div>
        )}
      </CardContent>
      {switching && (
        <ReasonDialog
          title={target === 'outside' ? tr('billing', 'order_line_outside_title', { name: switching.label }) : tr('billing', 'order_line_here_title', { name: switching.label })}
          description={target === 'outside' ? tr('billing', 'order_line_outside_description') : tr('billing', 'order_line_here_description')}
          minLength={3}
          maxLength={400}
          submitLabel={target === 'outside' ? tr('billing', 'order_line_send_outside') : tr('billing', 'order_line_do_here')}
          loading={change.isPending}
          onSubmit={(reason) => change.mutate({ id: switching.id, fulfilment: target, reason })}
          onClose={() => setSwitching(null)}
        />
      )}
    </Card>
  );
}
